<template>
  <div>
    <filter-action-modal
      name="plate-frequency-select"
      :title="$t('plate_group_select_plates_title')"
      :click-to-close="false"
      :filter-placeholder="$t('plate_group_search_plate_placeholder')"
      :filter-value="query"
      @update:filterValue="changeQuery"
      :filter-button-label="$t('searchOoo')"
      @search="search"
      :grid="plateGrid"
      grid-id="plateFrequencySelectionGrid"
      :selected-count="selectedPlate ? 1 : 0"
      @opened="syncGrid"
      :cancel-label="$t('back')"
      @cancel="close"
      :confirm-label="$t('proceed', { defaultValue: 'Prosseguir' })"
      :confirm-disabled="!canProceed"
      @confirm="proceed"
    />
    <filter-action-modal
      name="plate-frequency-values"
      :title="$t('dish_frequency', { defaultValue: 'Frequência de pratos' })"
      :click-to-close="false"
      :show-filter="false"
      width="520px"
      :cancel-label="$t('back', { defaultValue: 'Voltar' })"
      :cancel-disabled="saving"
      @cancel="back"
      :confirm-label="$t('save')"
      :confirm-disabled="saving"
      @confirm="save"
      :overlay-loading="saving"
      :overlay-loading-label="$t('loading', { defaultValue: 'Carregando...' })"
    >
      <form
        class="frequency-form"
        ref="form"
        @submit.prevent="save"
      >
        <div
          v-if="selectedPlate"
          class="frequency-plate"
        >
          <span
            class="frequency-plate-icon"
            aria-hidden="true"
            ><i class="fas fa-utensils"></i
          ></span>
          <div>
            <span class="frequency-plate-code"
              >{{ $t('dish_code') }}: {{ selectedPlate.CDPRATO }}</span
            >
            <strong>{{ selectedPlate.NMPRATO }}</strong>
          </div>
        </div>
        <div class="frequency-fields">
          <div
            v-for="field in fields"
            :key="field.name"
            class="frequency-field"
            :class="{ 'frequency-field-week': field.name === 'NRMAXFREQPRATSEM' }"
          >
            <label :for="field.name">{{ $t(field.label) }}</label>
            <div class="frequency-input-wrapper">
              <input
                :id="field.name"
                v-model.number="values[field.name]"
                type="number"
                min="1"
                step="1"
                required
                :disabled="saving"
                placeholder="—"
                aria-describedby="frequency-values-hint"
              />
            </div>
          </div>
        </div>
        <p
          id="frequency-values-hint"
          class="frequency-hint"
        >
          {{
            $t('frequency_values_hint', {
              defaultValue:
                'Informe quantidades inteiras maiores que zero. O mínimo mensal deve ser menor ou igual ao máximo mensal.'
            })
          }}
        </p>
        <p
          v-if="error"
          class="frequency-error"
          role="alert"
        >
          {{ error }}
        </p>
        <button
          type="button"
          class="button outline"
          :disabled="saving"
          @click="close"
        >
          {{ $t('cancel') }}
        </button>
      </form>
    </filter-action-modal>
  </div>
</template>

<script>
import FilterActionModal from './FilterActionModal.vue';
import RestrictionsService from '@/application/services/Restrictions';
import Util from '@/application/libs/util';
const SEARCH_DELAY_MS = 300;
const emptyValues = () => ({
  NRMINFREQPRATMES: '',
  NRMAXFREQPRATMES: '',
  NRMAXFREQPRATSEM: ''
});
const getErrorMessage = (error, fallback = '') =>
  (error && (error.error || error.message)) || fallback;
const uniquePlates = (plates) => {
  const byKey = new Map();
  plates.forEach((plate) => {
    const selectionKey = JSON.stringify([plate.CDFILAUXPRAT, plate.CDPRATO]);
    byKey.set(selectionKey, {
      ...plate,
      selectionKey
    });
  });
  return Array.from(byKey.values());
};
export default {
  name: 'PlateFrequencyModal',
  components: {
    FilterActionModal
  },
  props: {
    panel: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      query: '',
      plates: [],
      selectedPlate: null,
      validity: null,
      values: emptyValues(),
      loading: false,
      saving: false,
      error: '',
      plateGrid: {
        defaultColDef: {
          resizable: true,
          lockVisible: true,
          editable: false,
          sortable: true,
          filter: true
        },

        columnDefs: [
          {
            headerValueGetter: () => this.$t('tree'),
            field: 'CDFILAUXPRAT',
            maxWidth: 120,
            checkboxSelection: true
          },

          {
            headerValueGetter: () => this.$t('code'),
            field: 'CDPRATO',
            maxWidth: 120
          },

          {
            headerValueGetter: () => this.$t('name'),
            field: 'NMPRATO',
            minWidth: 380
          }
        ],
        rowSelection: 'single',
        suppressRowClickSelection: true,
        onRowClicked: this.onPlateRowClicked,
        onSelectionChanged: this.onPlateSelectionChanged
      },

      fields: [
        {
          name: 'NRMINFREQPRATMES',
          label: 'minimum_month'
        },

        {
          name: 'NRMAXFREQPRATMES',
          label: 'maximum_month'
        },

        {
          name: 'NRMAXFREQPRATSEM',
          label: 'maximum_week'
        }
      ]
    };
  },
  computed: {
    canProceed() {
      return !this.loading && Boolean(this.selectedPlate);
    }
  },
  watch: {
    loading: 'syncOverlay',
    'panel.selectedValidity': 'close'
  },
  created() {
    this.requestId = 0;
    this.searchTimer = null;
  },
  beforeDestroy() {
    this.cancelSearch();
  },
  methods: {
    open() {
      if (!this.panel.selectedValidity) {
        Util.showErrorNotification({
          title: this.$t('unable_to_add_dish_frequency'),
          text: this.$t('no_term_has_been_selected')
        });
        return;
      }

      this.validity = { ...this.panel.selectedValidity };
      this.query = '';
      this.plates = [];
      this.selectedPlate = null;
      this.values = emptyValues();
      this.error = '';
      this.$modal.show('plate-frequency-select');
      this.search();
    },

    close() {
      if (this.saving) {
        return;
      }

      this.cancelSearch();
      this.loading = false;
      this.$modal.hide('plate-frequency-select');
      this.$modal.hide('plate-frequency-values');
    },

    proceed() {
      if (!this.canProceed) {
        return;
      }

      this.$modal.hide('plate-frequency-select');
      this.$modal.show('plate-frequency-values');
    },

    back() {
      if (this.saving) {
        return;
      }

      this.$modal.hide('plate-frequency-values');
      this.$modal.show('plate-frequency-select');
    },

    changeQuery(value) {
      this.query = value;
      this.cancelSearch();
      this.loading = true;
      this.selectedPlate = null;

      const api = this.getPlateGridApi();
      if (api) {
        api.deselectAll();
      }
      this.searchTimer = setTimeout(() => this.search(), SEARCH_DELAY_MS);
    },

    cancelSearch() {
      this.requestId++;
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    },

    async search() {
      this.cancelSearch();
      const requestId = this.requestId;
      this.loading = true;
      this.selectedPlate = null;

      try {
        const results = await RestrictionsService.getPlate(
          this.query.trim(),
          this.validity.NRVIGENCIA
        );
        if (requestId !== this.requestId) {
          return;
        }
        this.plates = uniquePlates(results || []);
        this.syncGrid();
      } catch (error) {
        if (requestId !== this.requestId) {
          return;
        }
        this.plates = [];
        this.syncGrid();
        Util.showErrorNotification({
          title: this.$t('unable_to_add_dish_frequency'),
          text: getErrorMessage(error)
        });
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },

    getPlateGridApi() {
      const api = this.plateGrid.gridControl;
      return api && !(api.isDestroyed && api.isDestroyed()) ? api : null;
    },

    onPlateRowClicked({ node }) {
      if (!this.loading) {
        node.setSelected(!node.isSelected(), true);
      }
    },

    onPlateSelectionChanged({ api }) {
      const selectedRows = api.getSelectedRows();

      if (this.loading) {
        if (selectedRows.length) {
          api.deselectAll();
        }
        return;
      }

      this.selectedPlate = selectedRows[0] || null;
    },

    syncGrid() {
      const api = this.getPlateGridApi();

      if (!api) {
        return;
      }

      const selectedPlate = this.selectedPlate;
      this.plateGrid.setRowData(this.plates);

      if (selectedPlate && !this.loading) {
        api.forEachNode((node) => {
          if (node.data.selectionKey === selectedPlate.selectionKey) {
            node.setSelected(true, true);
          }
        });
      }
      this.syncOverlay();
    },

    syncOverlay() {
      const api = this.getPlateGridApi();
      
      if (!api) {
        return;
      }

      if (this.loading) {
        api.showLoadingOverlay();
      } else if (this.plates.length === 0) {
        api.showNoRowsOverlay();
      } else {
        api.hideOverlay();
      }
    },

    async save() {
      if (this.saving || !this.selectedPlate || !this.$refs.form.reportValidity()) {
        return;
      }

      this.saving = true;
      this.error = '';

      const grid = this.panel.gridConfiguration;

      try {
        const result = await RestrictionsService.savePlateFrequencyGridData(
          {
            CDPRATO: this.selectedPlate.CDPRATO,
            CDFILAUXPRAT: this.selectedPlate.CDFILAUXPRAT,
            ...this.values
          },

          'NRMAXFREQPRATSEM',
          grid.getRowData(),
          this.validity
        );
        if (!result || result.error) {
          throw result || new Error(this.$t('unable_to_save_dish_frequency'));
        }
      } catch (error) {
        this.error = getErrorMessage(error, this.$t('unable_to_save_dish_frequency'));
        return;
      } finally {
        this.saving = false;
        this.refreshFrequencyGrid();
      }
      this.close();
      Util.showSuccessNotification({
        title: this.$t('dish_frequency_saved')
      });
    },

    async refreshFrequencyGrid() {

      try {
        await this.panel.gridConfiguration.reloadData();
      } catch (error) {
        Util.showErrorNotification({
          title: this.$t('error', {
            defaultValue: 'Erro'
          }),
          text: getErrorMessage(error)
        });
      }
    }
  }
};
</script>

<style scoped>
.frequency-form {
  padding: 14px;
}

.frequency-plate {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 22px;
  background: #f4f6fa;
  border-radius: 8px;
}

.frequency-plate-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 8px;
  color: #212146;
}

.frequency-plate > div {
  min-width: 0;
}

.frequency-plate-code {
  display: block;
  color: #667085;
  font-size: 0.75rem;
  margin-bottom: 4px;
}

.frequency-plate strong {
  display: block;
  color: #212146;
  font-size: 0.95rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.frequency-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 16px;
}

.frequency-field-week {
  grid-column: 1 / -1;
}

.frequency-field label {
  display: block;
  margin-bottom: 8px;
  color: #344054;
  font-size: 0.85rem;
  font-weight: 600;
}

.frequency-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #cbd2dd;
  border-radius: 7px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.frequency-input-wrapper:focus-within {
  border-color: #212146;
  box-shadow: 0 0 0 3px rgba(33, 33, 70, 0.12);
}

.frequency-input-wrapper input {
  width: 100%;
  min-width: 0;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  color: #212146;
  font: inherit;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}

.frequency-input-wrapper input:focus {
  outline: none;
  box-shadow: none;
}

.frequency-input-wrapper input:disabled {
  color: #98a2b3;
  cursor: wait;
}

.frequency-input-wrapper input::placeholder {
  color: #98a2b3;
}

.frequency-hint {
  margin: 14px 0 20px;
  color: #667085;
  font-size: 0.78rem;
  line-height: 1.5;
}

.frequency-error {
  padding: 10px 12px;
  border-left: 3px solid #b01030;
  border-radius: 4px;
  background: #fff1f3;
  color: #b01030;
  font-size: 0.85rem;
  line-height: 1.5;
}

@media (max-width: 480px) {
  .frequency-form {
    padding: 8px;
  }
  .frequency-fields {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
</style>
